import React, { useEffect, useRef, useCallback } from 'react'
import cn from 'classnames'
import styles from 'components/votes/upvalue.module.scss'
import { Answer } from 'types/answers'
import type { Quest } from 'types/quests'
import useStore from 'state/knovStore'
import { UpvalueEffect } from './buttons/UpvalueEffect'
import { UpvalueButton } from './buttons/UpvalueButton'
import { useUpvalueAction } from './useUpvalueAction'
import { simpleUpvalueModalEnabledForSource } from 'lib/simpleUpvalueModal'

type UpvalueProps = {
    answer: Answer
    signalQuest?: Quest
    postLockRef: React.RefObject<HTMLDivElement>
    theme?: 'play'
    // Compact ordinals-chat presentation (the Answer UI's thin controls).
    thin?: boolean
    // Just the quick arrow, no options chevron beside it (radio chat rows).
    quickOnly?: boolean
    // Artifact surface (ordinals dock): presses carry the ordinal id so the
    // payout routes to the artifact's current owner, and boost detection keys
    // off that owner instead of the answer author.
    artifactOrdinalId?: string
    artifactOwnerId?: string
    source?: string
    resolveAnswer?: () => Promise<Answer | null>
    onOpenReactionPicker?: () => void
    onCloseReactionPicker?: () => void
    onReactionCommitted?: () => void
}

export default function Upvalue({
    answer,
    signalQuest,
    postLockRef,
    theme,
    thin = false,
    quickOnly = false,
    artifactOrdinalId,
    artifactOwnerId,
    source,
    resolveAnswer,
    onOpenReactionPicker,
    onCloseReactionPicker,
    onReactionCommitted,
}: UpvalueProps) {
    const effectEnabled = useStore(state => state.effectEnabled)
    const {
        currentUser,
        freeReactionAvailable,
        handleUpvalue,
        nextPressIsFree,
        showBoost,
        signalType,
        upvaluesEnabled,
    } = useUpvalueAction({
        answer,
        artifactOrdinalId,
        artifactOwnerId,
        source,
        resolveAnswer,
    })

    const signalButtonContainerRef = useRef<HTMLDivElement>(null)

    const animateLock = useCallback((ref: React.RefObject<HTMLDivElement>, scale = 1.15) => {
        if (!ref?.current) return
        const duration = 100
        const comp = ref.current
        comp.style.transition = `all ${duration / 1000}s ease-in-out`
        comp.style.transform = `scale(${scale})`
        new Promise(resolve => setTimeout(resolve, duration))
            .then(() => {
                comp.style.backgroundColor = ''
                comp.style.color = ''
                comp.style.transform = ''
                return new Promise(resolve => setTimeout(resolve, duration))
            })
            .then(() => {
                comp.style.transition = ''
            })
    }, [])

    const hasAnimated = useRef(false)
    useEffect(() => {
        if (!hasAnimated.current) {
            hasAnimated.current = true
            return
        }
        animateLock(postLockRef)
    }, [answer.bsv_upvalue_amt, animateLock, postLockRef])

    return (
        <div
            className={cn(
                styles.upvalueComp,
                theme === 'play' && styles.playTheme,
                thin && styles.thinVariant,
            )}
        >
            <div className={styles.signalRelativeContainer}>
                <UpvalueEffect
                    containerRef={signalButtonContainerRef}
                    effectEnabled={effectEnabled}
                >
                    <UpvalueButton
                        signalType={signalType}
                        showBoost={showBoost}
                        theme={theme}
                        onOpenReactionPicker={onOpenReactionPicker}
                        onCloseReactionPicker={onCloseReactionPicker}
                        onReactionCommitted={onReactionCommitted}
                        handleLockOrUpvalue={handleUpvalue}
                        animateLock={animateLock}
                        currentUser={currentUser}
                        answer={answer}
                        signalQuest={signalQuest}
                        containerRef={signalButtonContainerRef}
                        nextPressIsFree={nextPressIsFree}
                        upvaluesEnabled={upvaluesEnabled}
                        freeReactionAvailable={freeReactionAvailable}
                        useSimpleUpvalueModal={simpleUpvalueModalEnabledForSource(source)}
                        quickOnly={quickOnly}
                    />
                </UpvalueEffect>
            </div>
        </div>
    )
}
